import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, IconButton, Divider, Grid, Table, TableBody, TableCell, TableRow, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import logo from "../../../assets/images/hashstack-logo-orange-black.png";

/* ================= HELPERS ================= */
const num = (v) =>
    Number(v ?? 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const txt = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const dt = (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "-");

const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

/* ================= REUSABLE TABLE ================= */
const DetailedTable = ({ rows, totalLabel, totalValue }) => (
    <Table size="small">
        <TableBody>
            <TableRow>
                <TableCell sx={{ color: 'text.secondary', py: 0.5 }}><b>Description</b></TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary', py: 0.5 }}><b>Amount</b></TableCell>
            </TableRow>
            {rows.map((r) => (
                <TableRow key={r.label}>
                    <TableCell sx={{ borderBottom: '1px solid #eee', py: 0.5 }}>{r.label}</TableCell>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee', py: 0.5 }}>₹{num(r.cur)}</TableCell>
                </TableRow>
            ))}
            {totalLabel && (
                <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{totalLabel}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{num(totalValue)}</TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
);

/* ================= COMPONENT ================= */
function PayslipPreviewView({ data, onBack }) {
    const { employeePersonalId, month, year } = data || {};

    const [payslipData, setPayslipData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (employeePersonalId && month && year) fetchPayslip();
        // eslint-disable-next-line
    }, []);

    const fetchPayslip = async () => {
        setLoading(true);
        try {
            // Pass arguments by position, not by name
            const res = await getData(API_ENDPOINTS.getEmployeePayslipByDateRange(
                month,              // startMonth
                year,               // startYear
                month,              // endMonth
                year,               // endYear
                employeePersonalId  // employeeId (Note: Check if your endpoint helper actually handles this 5th param)
            ));

            if (res?.statusCode === 200) {
                setPayslipData(res.result?.employees?.[0]);
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async () => {
        const el = document.getElementById("payslip-print");
        const canvas = await html2canvas(el, { scale: 2 });
        const img = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");
        const w = pdf.internal.pageSize.getWidth();
        const ratio = w / canvas.width;

        pdf.addImage(img, "PNG", 0, 5, canvas.width * ratio, canvas.height * ratio);
        pdf.save(`Payslip-${month}-${year}.pdf`);
    };

    if (loading || !payslipData) {
        return (
            <Box display="flex" justifyContent="center" mt={3}>
                <CircularProgress />
            </Box>
        );
    }

    const slip = payslipData.payslips?.[0] || {};
    const earnings = slip.earnings || {};
    const deductions = slip.deductions || {};
    const leaves = slip.leaves || {};
    const bank = payslipData.bankDetails || {};
    const statutory = payslipData.statutoryDetails || {};

    // Calculate pre-LOP gross for detailed view
    const preLopGross = (parseFloat(earnings.basic) || 0) +
        (parseFloat(earnings.hra) || 0) +
        (parseFloat(earnings.conveyanceAllowance) || 0) +
        (parseFloat(earnings.specialAllowance) || 0) +
        (parseFloat(earnings.otherAllowance) || 0) +
        (parseFloat(earnings.performanceBonus) || 0);

    /* ================= ROW CONFIG ================= */
    const earningsRows = [
        { label: "Basic Pay", cur: earnings.basic },
        { label: "House Rent Allowance (HRA)", cur: earnings.hra },
        { label: "Conveyance Allowance", cur: earnings.conveyanceAllowance },
        { label: "Special Allowance", cur: earnings.specialAllowance },
        { label: "Other Allowance", cur: earnings.otherAllowance },
    ].filter(r => parseFloat(r.cur) > 0);

    if (parseFloat(earnings.performanceBonus) > 0) {
        earningsRows.push({ label: "Performance Bonus", cur: earnings.performanceBonus });
    }

    const deductionRows = [
        { label: "Provident Fund (PF)", cur: deductions.pfEmployee },
        { label: "Employee State Insurance (ESI)", cur: deductions.esiEmployee },
        { label: "Professional Tax (PT)", cur: deductions.professionalTax },
        { label: "Income Tax", cur: deductions.incomeTax },
        { label: "Education Cess (4%)", cur: deductions.cess },
    ].filter(r => r.cur !== undefined && r.cur !== null && r.cur !== "");

    // If Income Tax breakdown is missing but TDS exists, show TDS
    if (parseFloat(deductions.tds) > 0 && !(parseFloat(deductions.incomeTax) > 0)) {
        deductionRows.push({ label: "Income Tax (TDS)", cur: deductions.tds });
    }

    if (parseFloat(leaves.lopAmount) > 0) {
        deductionRows.push({
            label: `Loss of Pay (LOP) - ${leaves.lopCount || 0} Days`,
            cur: leaves.lopAmount
        });
    }

    const additionRows = [
        { label: "Claim Reimbursement", cur: earnings.reimbursementAmount },
        { label: "Leave Encashment", cur: earnings.leaveEncashmentAmount },
    ].filter(r => parseFloat(r.cur) > 0);

    const totalAdditions = (parseFloat(earnings.reimbursementAmount) || 0) + (parseFloat(earnings.leaveEncashmentAmount) || 0);
    const totalDeductions = (parseFloat(deductions.totalDeductions) || 0) + (parseFloat(leaves.lopAmount) || 0);

    return (
        <Box sx={{ background: "#f4f6f8", minHeight: "100vh", p: 1 }}>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                <IconButton onClick={onBack} title="Go Back"><ArrowBackIcon /></IconButton>
                <IconButton onClick={downloadPDF} title="Download PDF"><DownloadIcon /></IconButton>
            </Box>

            <Paper id="payslip-print" sx={{ p: 3, maxWidth: 900, mx: "auto", borderRadius: 2 }}>
                {/* HEADER */}
                <Box display="flex" alignItems="center" mb={1}>
                    <img
                        src={logo}
                        alt="Logo"
                        style={{ height: "75px", maxWidth: "150px", objectFit: "contain" }}
                    />

                    <Box flex={1} textAlign="center">
                        <Typography variant="h6" fontWeight="bold">Hashstack Solutions</Typography>

                        <Typography variant="caption" display="block" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                            <b>Registered Office:</b> Plot 1017, Road No 2/6, Matrusrinagar, Miyapur, Hyderabad - 500049<br />
                            <b>Branch Office:</b> Plot No 19, APIIC Hill No 3, IT/ITES SEZ, Madhurawada, Visakhapatnam - 530045
                        </Typography>

                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textDecoration: 'underline', mt: 1 }}>
                            PAYSLIP FOR THE MONTH OF {String(MONTHS[month - 1] || month || "").toUpperCase()} {year || ""}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* BASIC INFO */}
                <Grid container spacing={4}>
                    <Grid size={{ xs: 6 }}>
                        <Table size="small">
                            <TableBody>
                                {[
                                    ["Employee ID", payslipData.employeeId],
                                    ["Employee Name", payslipData.employeeName],
                                    ["Department", payslipData.department],
                                    ["Designation", payslipData.designation],
                                    ["Date of Joining", dt(payslipData.dateOfJoining)],
                                ].map(([k, v]) => (
                                    <TableRow key={k}><TableCell sx={{ py: 0.3, border: 0 }}>{k}</TableCell><TableCell align="right" sx={{ py: 0.3, border: 0 }}><b>{txt(v)}</b></TableCell></TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Table size="small">
                            <TableBody>
                                {[
                                    ["Bank Name", bank.bankName],
                                    ["Account No", bank.accountNumber],
                                    ["IFSC Code", bank.ifscCode],
                                    ["PAN Number", statutory.pan],
                                    ["UAN Number", statutory.uanNumber],
                                ].map(([k, v]) => (
                                    <TableRow key={k}><TableCell sx={{ py: 0.3, border: 0 }}>{k}</TableCell><TableCell align="right" sx={{ py: 0.3, border: 0 }}><b>{txt(v)}</b></TableCell></TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* EARNINGS & DEDUCTIONS */}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>EARNINGS</Typography>
                        <DetailedTable rows={earningsRows} totalLabel="Gross Earnings" totalValue={preLopGross} />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'error.main' }}>DEDUCTIONS</Typography>
                        <DetailedTable rows={deductionRows} totalLabel="Total Deductions" totalValue={totalDeductions} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                    {additionRows.length > 0 && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'success.main' }}>ADJUSTMENTS / ADDITIONS</Typography>
                                <DetailedTable rows={additionRows} totalLabel="Total Additions" totalValue={totalAdditions} />
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/* NET SALARY SUMMARY */}
                <Box sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: '#f8fff8',
                    border: '2px solid #c8e6c9',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                }}>
                    <Box sx={{ width: '100%', maxWidth: 300 }}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Current Month Gross:</Typography>
                            <Typography variant="body2">₹{num(preLopGross)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Total Deductions (-):</Typography>
                            <Typography variant="body2">₹{num(totalDeductions)}</Typography>
                        </Box>
                        {totalAdditions > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="body2">Total Additions (+):</Typography>
                                <Typography variant="body2">₹{num(totalAdditions)}</Typography>
                            </Box>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight="bold">NET PAYABLE:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="success.dark">₹{num(slip.netSalary)}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        This is a system generated payslip for Hashstack Solutions and does not require a physical signature.
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

export default PayslipPreviewView;